import type { Briefing } from '@acasa/shared'
import { useEffect, useState } from 'react'

/** A run reads every document in one model call: minutes, not seconds. */
const EXPECTED_MS = 4 * 60_000
const OVERRUN_MS = 6 * 60_000

/**
 * Elapsed time against the expected duration — openly an ESTIMATE, as in
 * Bild-KI's `JobProgress`. The model reports no progress, so the bar is the
 * clock, stops short of full, and says so when a run takes longer than usual
 * instead of pretending to be nearly done.
 */
export function BriefingProgress({ briefing }: { briefing: Briefing }) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  const startedAt = briefing.startedAt ? Date.parse(briefing.startedAt) : null
  const elapsedMs = startedAt === null ? 0 : Math.max(0, now - startedAt)
  const overrun = elapsedMs > OVERRUN_MS
  const ratio = startedAt === null ? 0 : Math.min(0.92, elapsedMs / EXPECTED_MS)
  const minutes = Math.floor(elapsedMs / 60_000)
  const seconds = Math.floor((elapsedMs % 60_000) / 1000)
  const elapsed = `${minutes}:${String(seconds).padStart(2, '0')} min`

  return (
    <div className="border-line bg-surface border px-4 py-4" aria-live="polite">
      <p className="text-ink text-sm">
        {briefing.status === 'queued'
          ? 'Wartet, bis das vorherige Briefing fertig ist …'
          : 'Die Unterlagen werden ausgewertet …'}
      </p>
      <div
        className="bg-open mt-3 h-1 w-full overflow-hidden"
        role="progressbar"
        aria-label="Geschätzter Fortschritt"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(ratio * 100)}
      >
        <div
          className="bg-brand h-full transition-[width] duration-1000 ease-linear"
          style={{ width: `${Math.round(ratio * 100)}%` }}
        />
      </div>
      <p className="text-faint tnum mt-2 text-[11px]">
        {briefing.status === 'queued'
          ? 'Briefings laufen nacheinander. Die Seite aktualisiert sich von selbst.'
          : overrun
            ? `läuft seit ${elapsed} — dauert länger als üblich`
            : `läuft seit ${elapsed} · ca. 2–5 Minuten`}
      </p>
    </div>
  )
}
